import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import forEach from 'lodash/forEach'
import { createPopper, Instance as PopperInstance } from '@popperjs/core'
import { BButton, BListGroup, BListGroupItem } from 'bootstrap-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { KFormInput } from '@kwixeo/form-vue'

@Component({
  components: {
    BButton, BListGroup, BListGroupItem,
    FontAwesomeIcon,
    KFormInput
  }
})
export default class TaskElementSearch extends Vue {
  @Prop({ default: () => [] }) value!: KwixeoTasks.TaskElements
  @Prop({ default: null }) only!: string|string[]|null
  @Prop({ default: null }) size!: string|null
  @Prop({ default: true }) withModal!: boolean

  private searchTerm: string|null = null
  private timeout: number|null = null
  private results: null|KwixeoInterface.SearchResult = null
  private linkedContacts: { [key: number]: KwixeoTasks.Contacts } = {}
  private loading = false
  private dropdownState = false
  private showLinkedContact = false
  private inputFocusTimeout: number|null = null
  private popperInstance: PopperInstance|null = null
  private localValue: KwixeoInterface.SearchResultData[] = []

  get excludeElement (): string {
    let params = ''
    this.localValue.forEach((val, i) => {
      if (params !== '') {
        params += '&'
      }
      params += 'excludeElement[' + i + '][type]=' + val.type
      params += '&excludeElement[' + i + '][id]=' + val.data.id
    })
    return params
  }

  get includeElement (): string {
    return 'include[0][type]=customer&include[0][include][0]=contacts'
  }

  get computedLinkedContacts (): KwixeoTasks.Contacts {
    const contacts: KwixeoTasks.Contacts = []
    forEach(this.linkedContacts, els => {
      contacts.push(...els)
    })
    return contacts.filter((v, i, a) => this.localValue.find(s => s.type === 'contact' && s.data.td !== v.id) === undefined && a.findIndex(t => (t.id === v.id)) === i)
  }

  @Watch('value', { immediate: true })
  private async watchValue (value: KwixeoTasks.TaskElements): Promise<void> {
    if (JSON.stringify(value) !== JSON.stringify(this.localValue)) {
      this.localValue = JSON.parse(JSON.stringify(value))
      await this.fetchLinkedContacts()
    }
  }

  private async fetchLinkedContacts (): Promise<void> {
    const customers = this.localValue.filter(value => {
      return value.type === 'customer' && this.linkedContacts[value.data.id] === undefined
    }).map(value => value.data.id)
    if (customers.length === 0) {
      return
    }

    this.loading = true
    await this.$kwTasks.axios.get('/customer/contact', {
      params: {
        filter: {
          with_customer: customers,
        },
        include: 'customers',
        sort: 'civility.name:asc'
      }
    }).then(response => {
      response.data.data.forEach((contact: KwixeoTasks.Contact) => {
        contact.customers.forEach((customer: KwixeoCustomers.Customer) => {
          if (this.linkedContacts[customer.id] === undefined) {
            Vue.set(this.linkedContacts, customer.id, [])
          }
          this.linkedContacts[customer.id].push(contact)
        })
      })
    })
    this.loading = false
  }

  @Watch('localValue')
  watchLocalValue (localValue: KwixeoInterface.SearchResultData[]): void {
    if (JSON.stringify(this.value) !== JSON.stringify(localValue)) {
      this.$emit('input', JSON.parse(JSON.stringify(localValue)))
    }
  }

  @Watch('dropdownState')
  private watchDropdownState (state: boolean): void {
    if (state) {
      this.initDropdown()
    } else if (this.popperInstance) {
      this.popperInstance.destroy()
      this.results = null
    }
  }

  private initDropdown (): void {
    this.popperInstance = createPopper(
      this.$refs.options as HTMLElement,
      this.$refs.list as HTMLElement,
      { placement: 'bottom' }
    )
  }

  private onInput (val: string): void {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    if (!val) {
      this.showLinkedContact = true
      this.dropdownState = false
      this.loading = false
      return
    }

    this.showLinkedContact = false
    this.results = null
    this.dropdownState = true
    this.loading = true
    this.timeout = setTimeout(this.search, 400)
  }

  private clearSearch (): void {
    this.results = null
    this.dropdownState = false
    this.loading = false
    this.searchTerm = null
  }

  private search (): void {
    if (!this.searchTerm) {
      this.timeout = null
      this.loading = false
      return
    }

    this.$kwTasks.axios.get('/task/search-elements?' + this.excludeElement + '&' + this.includeElement, {
      params: {
        total: 10,
        search: this.searchTerm,
        elements: this.only
      }
    })
      .then(response => {
        this.results = response.data
      })
      .catch(error => {
        throw new Error(error)
      })
      .finally(() => {
        this.timeout = null
        this.loading = false
      })
  }

  private selectEntry (entry: KwixeoInterface.SearchResultData): void {
    this.clearSearch()
    this.selectedEntry(JSON.parse(JSON.stringify(entry)))
  }

  private addLinkedContact (contact: KwixeoTasks.Contact): void {
    this.selectEntry({
      type: 'contact',
      title: contact.fullname,
      data: contact
    })
    this.showLinkedContact = false
  }

  private selectedEntry (entry: KwixeoInterface.SearchResultData): void {
    const affair = entry.data.affair
    if (affair) {
      entry = {
        type: 'affair',
        title: affair.reference,
        data: affair
      }
    }

    if (!this.localValue.find(val => val.type === entry.type && val.data.id === entry.data.id)) {
      this.localValue.push(entry)

      if (entry.type === 'customer') {
        Vue.set(this.linkedContacts, entry.data.id, entry.data.contacts)
      }
    }
  }

  private unselectValue (index: number): void {
    const entry = this.localValue[index]
    if (entry.type === 'customer') {
      Vue.delete(this.linkedContacts, entry.data.id)
    }

    this.localValue.splice(index, 1)
  }

  private blurInput (): void {
    if (this.inputFocusTimeout) {
      clearTimeout(this.inputFocusTimeout)
      this.inputFocusTimeout = null
    }

    this.showLinkedContact = true
  }

  private unblurInput (): void {
    this.inputFocusTimeout = setTimeout(() => {
      this.showLinkedContact = false
    }, 200)
  }
}
